/**
 * ManaBaseHealth - Mana base health summary component
 *
 * Shows basic/nonbasic land ratio, utility lands count,
 * and mana dork/rock breakdown.
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';

import InfoButton, { InfoList, InfoSection, InfoTip } from '@/components/InfoButton/InfoButton';

import type { ManaBaseHealth as ManaBaseHealthData } from '../../hooks/useManaBaseAnalysis';

export interface ManaBaseHealthProps {
  data: ManaBaseHealthData;
  /** Compact mode for left panel */
  compact?: boolean;
}

function ManaBaseHealthInfo(): ReactElement {
  return (
    <>
      <InfoSection title="What is this?">
        <p>
          This section breaks down your mana sources into categories so you can understand your mana base composition and identify potential
          issues.
        </p>
      </InfoSection>

      <InfoSection title="Total Mana Sources">
        <p>
          The total count of everything that produces mana: lands + mana dorks + mana rocks. More sources mean more consistent mana, but too
          many can flood you with mana and not enough spells.
        </p>
      </InfoSection>

      <InfoSection title="Basic vs Nonbasic Lands">
        <InfoList
          items={[
            'Basic lands always enter untapped and have no restrictions',
            'Nonbasic lands often have drawbacks (enter tapped, life loss, etc.)',
            'Some cards punish nonbasics (Blood Moon, Back to Basics)',
            'A healthy mix depends on your format and color needs',
          ]}
        />
      </InfoSection>

      <InfoSection title="Utility Lands">
        <p>
          Lands with abilities beyond mana production (card draw, removal, token creation, etc.). They add flexibility but typically produce
          colorless or enter tapped.
        </p>
      </InfoSection>

      <InfoSection title="Mana Dorks & Rocks">
        <InfoList
          items={[
            'Mana dorks are creatures that tap for mana (Llanowar Elves, Birds of Paradise)',
            'Mana rocks are artifacts that tap for mana (Sol Ring, Arcane Signet)',
            'Both accelerate you but are vulnerable to removal',
            'Count as roughly 0.5 lands each for reliability calculations',
          ]}
        />
      </InfoSection>

      <InfoTip>
        In Commander, aim for 10+ mana rocks/dorks alongside 33-38 lands. In 60-card formats, 4-8 additional mana sources is typical for
        ramp strategies.
      </InfoTip>
    </>
  );
}

export default function ManaBaseHealth({ data, compact = true }: ManaBaseHealthProps): ReactElement {
  const { basicLands, nonBasicLands, utilityLands, manaDorks, manaRocks, totalSources } = data;

  const totalLands = basicLands + nonBasicLands;
  const basicPercent = totalLands > 0 ? Math.round((basicLands / totalLands) * 100) : 0;

  return (
    <div className="space-y-3">
      {/* Total sources summary */}
      <div className="rounded-lg bg-zinc-800/50 p-3 text-center">
        <div className="text-pearl text-2xl font-bold">{totalSources}</div>
        <div className="flex items-center justify-center gap-1">
          <span className="text-ash text-xs">Total Mana Sources</span>
          <InfoButton title="Mana Base Overview">
            <ManaBaseHealthInfo />
          </InfoButton>
        </div>
      </div>

      {/* Land breakdown */}
      <div className="rounded-lg bg-zinc-800/30 p-3">
        <div className="text-ash mb-2 text-xs font-medium tracking-wider uppercase">Lands</div>

        {/* Basic/Nonbasic ratio bar */}
        <div className="mb-2">
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="text-emerald-400">{basicLands} Basic</span>
            <span className="text-blue-400">{nonBasicLands} Nonbasic</span>
          </div>
          <div className="flex h-2 overflow-hidden rounded-full bg-zinc-700">
            {totalLands > 0 && (
              <>
                <div className="bg-emerald-500 transition-all duration-300" style={{ width: `${basicPercent}%` }} />
                <div className="bg-blue-500 transition-all duration-300" style={{ width: `${100 - basicPercent}%` }} />
              </>
            )}
          </div>
          <div className="text-ash mt-1 text-center text-[10px]">{basicPercent}% basic lands</div>
        </div>

        {/* Utility lands */}
        {utilityLands > 0 && (
          <div className="text-ash flex items-center justify-between border-t border-zinc-700/50 pt-2 text-xs">
            <span>Utility Lands</span>
            <span className="text-silver font-medium">{utilityLands}</span>
          </div>
        )}
      </div>

      {/* Mana producers (non-land) */}
      {(manaDorks > 0 || manaRocks > 0) && (
        <div className="rounded-lg bg-zinc-800/30 p-3">
          <div className="text-ash mb-2 text-xs font-medium tracking-wider uppercase">Non-Land Sources</div>
          <div className={clsx('grid gap-2', compact ? 'grid-cols-2' : 'grid-cols-2')}>
            <div className="rounded bg-zinc-800/50 p-2 text-center">
              <div className="text-silver text-lg font-semibold">{manaDorks}</div>
              <div className="text-ash text-[10px]">Mana Dorks</div>
            </div>
            <div className="rounded bg-zinc-800/50 p-2 text-center">
              <div className="text-silver text-lg font-semibold">{manaRocks}</div>
              <div className="text-ash text-[10px]">Mana Rocks</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
